import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Radio, Video, Phone, Building2, Settings as SettingsIcon, ChevronRight, Gift as GiftIcon } from "lucide-react";
import { CoinIcon, RubyIcon } from "@/components/Icons";
import { toast } from "sonner";
import api, { errText, TIER_COLORS, tierImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import Avatar from "@/components/Avatar";
import BottomNav from "@/components/BottomNav";
import PostsSection from "@/components/PostsSection";
import TopFans from "@/components/TopFans";

export default function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth(); // eslint-disable-line no-unused-vars
  const { requestCall } = useCall();
  const targetId = userId || user?.id;
  const isMe = targetId === user?.id;
  const [data, setData] = useState(null);
  const [cs, setCs] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!targetId) return;
    api.get(`/users/${targetId}`).then(({ data: d }) => {
      setData(d);
      if (targetId === user?.id) {
        setCs({ calls_enabled: d.user.calls_enabled, call_rate_video: d.user.call_rate_video, call_rate_audio: d.user.call_rate_audio });
        api.get("/calls/history").then(({ data: h }) => setHistory(h.calls)).catch(() => {});
      }
    }).catch(() => {});
  }, [targetId, user?.id]);

  const saveCallSettings = async () => {
    try {
      await api.put("/calls/settings", { ...cs, call_rate_video: Number(cs.call_rate_video), call_rate_audio: Number(cs.call_rate_audio) });
      toast.success(cs.calls_enabled ? "You're now accepting 1:1 calls 📞" : "Calls disabled");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const toggleFollow = async () => {
    try {
      const { data: res } = await api.post(`/users/${targetId}/follow`);
      setData((d) => ({
        ...d, is_following: res.following,
        user: { ...d.user, followers_count: d.user.followers_count + (res.following ? 1 : -1) },
      }));
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!data) {
    return <div className="min-h-screen max-w-md mx-auto flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" /></div>;
  }

  const p = data.user;
  const tierColor = TIER_COLORS[p.tier];
  const charm = Math.max(1, Math.floor(Math.pow((p.diamonds || 0) / 50 + 1, 0.55)));

  const menuRows = [
    { key: "vip", testid: "profile-vip-button", icon: "👑", label: "VIP Tiers", accent: "#F5A623", to: "/vip" },
    { key: "navaratna", testid: "profile-navaratna-button", icon: "💠", label: `Navaratna Ladder${p.tier ? ` · ${p.tier}` : ""}`, accent: "#00E0C7", to: "/navaratna" },
    { key: "earnings", testid: "profile-earnings-button", icon: "♦️", label: "Earnings", accent: "#FF4D8D", to: "/earnings" },
    { key: "rewards", testid: "profile-rewards-button", icon: "🎁", label: "Daily Rewards", accent: "#FF2E97", to: "/rewards" },
    { key: "agency", testid: "profile-agency-button", icon: "🏢", label: "Agency", accent: "#A03CF5", to: "/agency" },
  ];

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-5 pb-28">
      <div className="flex items-center justify-between mb-2 relative z-10">
        <button data-testid="profile-back" onClick={() => navigate("/")} className="glass-chip w-9 h-9 flex items-center justify-center text-white/80"><ArrowLeft size={16} /></button>
        {isMe ? (
          <button data-testid="settings-button" onClick={() => navigate("/settings")} className="glass-chip w-9 h-9 flex items-center justify-center text-[#FFD86B]">
            <SettingsIcon size={16} />
          </button>
        ) : <span className="w-9" />}
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center text-center">
        <div className="relative mt-1">
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-40 rounded-full blur-3xl opacity-60 pointer-events-none"
            style={{ background: "radial-gradient(ellipse, rgba(160,60,245,0.5), rgba(255,46,151,0.25), transparent 70%)" }} />
          <div className="relative rounded-full p-[3px]" style={{ background: "conic-gradient(from 200deg, #FF2E97, #A03CF5, #FFD86B, #FF2E97)" }}>
            <Avatar src={p.avatar} size={96} />
          </div>
          <button data-testid="level-pill" onClick={() => isMe && navigate("/level")}
            className="absolute -top-1 -right-6 glass-chip px-2.5 py-1 text-[10px] font-extrabold text-white z-10">Lv.{p.level}</button>
          {p.is_vip && (
            <span className="absolute -bottom-1 -right-4 rotate-6 bg-[#FFD86B] text-[#0B0B14] text-[9px] font-extrabold px-2 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,216,107,0.7)] z-10">👑 VIP</span>
          )}
        </div>
        <h1 className="text-xl font-extrabold mt-3 flex items-center gap-1.5" data-testid="profile-name">
          {p.name} <span className="text-[#A03CF5]">✔</span>
        </h1>
        <p className="text-[#6E6683] text-xs">@{p.username}</p>
        <div className="flex gap-2 mt-3.5 relative z-10">
          <span className="text-[11px] font-extrabold px-3.5 py-1.5 rounded-full text-white flex items-center gap-1" data-testid="charm-pill"
            style={{ background: "linear-gradient(90deg, #FF4D8D, #E23670)" }}>
            💖 Charm {charm.toLocaleString()}
          </span>
          <button data-testid="tier-pill" onClick={() => navigate("/vip")}
            className="text-[11px] font-extrabold px-3.5 py-1.5 rounded-full text-[#0B0B14] active:scale-95 transition-transform flex items-center gap-1"
            style={{ background: `linear-gradient(90deg, ${tierColor || "#FFD86B"}, #FFD86B)` }}>
            {p.tier && tierImage(p.tier) ? <img src={tierImage(p.tier)} alt="" className="inline w-4 h-4 align-[-3px]" /> : "👑"}{p.tier}
          </button>
        </div>
        {p.bio && <p className="text-[#B8B0C9] text-xs mt-2.5 max-w-[260px]">{p.bio}</p>}

        <div className="w-full bg-[#1E1830] border border-white/[0.06] rounded-3xl mt-5 py-4 flex" data-testid="profile-stats-card">
          <button data-testid="stat-fans" onClick={() => isMe && navigate("/fans?tab=fans")} className="flex-1 text-center">
            <p className="text-white font-extrabold text-lg leading-tight">{p.followers_count?.toLocaleString()}</p>
            <p className="text-[11px] text-[#6E6683] font-semibold">Fans</p>
          </button>
          <span className="w-px bg-white/[0.07]" />
          <button data-testid="stat-following" onClick={() => isMe && navigate("/fans?tab=following")} className="flex-1 text-center">
            <p className="text-white font-extrabold text-lg leading-tight">{p.following_count?.toLocaleString()}</p>
            <p className="text-[11px] text-[#6E6683] font-semibold">Following</p>
          </button>
          <span className="w-px bg-white/[0.07]" />
          <button data-testid="stat-diamonds" onClick={() => isMe && navigate("/earnings")} className="flex-1 text-center">
            <p className="text-[#00E0C7] font-extrabold text-lg leading-tight flex items-center justify-center gap-1">
              <img src="/assets/ruby-gem.png" alt="" className="w-4 h-4 object-contain" style={{ filter: "hue-rotate(168deg) saturate(1.15)" }} />
              {p.diamonds?.toLocaleString()}
            </p>
            <p className="text-[11px] text-[#6E6683] font-semibold">Rubies</p>
          </button>
        </div>

        <p className="w-full text-left text-[#B8B0C9] text-xs font-semibold mt-5 mb-2">Badge Shelf</p>
        <div className="w-full flex items-center gap-2.5 overflow-x-auto no-scrollbar pb-1">
          {p.tier && tierImage(p.tier) && (
            <span className="relative w-12 h-12 shrink-0 flex items-center justify-center" data-testid="profile-tier-badge">
              <span className="absolute inset-0 rounded-full blur-md opacity-60 pointer-events-none"
                style={{ background: `radial-gradient(circle, ${TIER_COLORS[p.tier] || "#FFD86B"}66, transparent 70%)` }} />
              <img src={tierImage(p.tier)} alt={p.tier} className="relative w-12 h-12 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]" />
            </span>
          )}
          {["👑", "🏆", "⭐", "💖", "🎖️"].map((b) => (
            <span key={b} className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-lg gold-hairline"
              style={{ background: "radial-gradient(circle at 30% 25%, rgba(255,216,107,0.18), rgba(30,24,48,0.9))" }}>{b}</span>
          ))}
        </div>

        {!isMe && (
          <>
          <div className="flex gap-2 mt-5 w-full">
            <button data-testid="profile-follow-button" onClick={toggleFollow}
              className={`flex-1 py-3 rounded-full font-bold text-sm ${data.is_following ? "bg-white/10 text-white/80" : "btn-gradient"}`}>
              {data.is_following ? "Following" : "+ Follow"}
            </button>
            <button data-testid="profile-message-button" onClick={() => navigate(`/messages/${p.id}`)}
              className="flex-1 py-3 rounded-full font-bold text-sm bg-[#1E1830] gold-hairline text-[#FFD86B] flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
              💬 Message
            </button>
            {data.live_stream_id && (
              <button data-testid="profile-watch-live" onClick={() => navigate(`/stream/${data.live_stream_id}`)}
                className="flex-1 py-3 rounded-full font-bold text-sm bg-[#FF3B5C] text-white flex items-center justify-center gap-1.5">
                <Radio size={15} /> LIVE
              </button>
            )}
          </div>
          {p.calls_enabled && (
            <div className="flex gap-2 mt-2 w-full">
              <button data-testid="profile-video-call" onClick={() => requestCall(p, "video")}
                className="flex-1 py-3 rounded-2xl font-bold text-xs bg-[#A03CF5]/10 border border-[#A03CF5]/50 text-[#CFA3FF] flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
                <Video size={14} /> Video Call · <CoinIcon size={12} />{p.call_rate_video}/min
              </button>
              <button data-testid="profile-audio-call" onClick={() => requestCall(p, "audio")}
                className="flex-1 py-3 rounded-2xl font-bold text-xs bg-[#00E0C7]/10 border border-[#00E0C7]/50 text-[#00E0C7] flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform">
                <Phone size={14} /> Voice Call · <CoinIcon size={12} />{p.call_rate_audio}/min
              </button>
            </div>
          )}
          </>
        )}

        {isMe && (
          <>
            <button data-testid="profile-go-live" onClick={() => navigate("/go-live")}
              className="mt-5 w-full btn-gradient font-bold py-3.5 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
              <Radio size={16} /> Go Live
            </button>
            <div className="w-full space-y-2.5 mt-4">
              {menuRows.map((r) => (
                <button key={r.key} data-testid={r.testid} onClick={() => navigate(r.to)}
                  className="w-full bg-[#1E1830] border border-white/[0.06] rounded-[20px] px-4 py-3.5 flex items-center gap-3 active:scale-[0.98] transition-transform">
                  <span className="w-9 h-9 rounded-2xl flex items-center justify-center text-base shrink-0"
                    style={{ background: `${r.accent}1E`, border: `1px solid ${r.accent}45` }}>{r.icon}</span>
                  <span className="flex-1 text-left text-white text-sm font-bold">{r.label}</span>
                  <ChevronRight size={16} className="text-[#6E6683]" />
                </button>
              ))}
            </div>
          </>
        )}
      </motion.div>

      {isMe && cs && (
        <>
          <div className="bg-[#1E1830] border border-white/[0.06] rounded-[20px] p-4 mt-4" data-testid="call-settings">
            <label className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-white flex items-center gap-2"><Phone size={14} className="text-[#00E0C7]" /> 1:1 Call Settings</span>
              <button data-testid="calls-enabled-toggle" onClick={() => setCs((c) => ({ ...c, calls_enabled: !c.calls_enabled }))}
                className={`w-10 rounded-full p-0.5 transition-colors ${cs.calls_enabled ? "gradient-primary" : "bg-white/10"}`} style={{ height: 22 }}>
                <span className={`block bg-white rounded-full transition-transform ${cs.calls_enabled ? "translate-x-5" : ""}`} style={{ width: 18, height: 18 }} />
              </button>
            </label>
            <p className="text-[11px] text-[#6E6683] mb-3">Accept paid 1:1 calls from fans</p>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-[10px] text-[#6E6683] font-semibold block mb-1">Video · <CoinIcon size={10} className="align-[-1px]" /> per min</label>
                <input data-testid="rate-video-input" type="number" min={1} value={cs.call_rate_video}
                  onChange={(e) => setCs((c) => ({ ...c, call_rate_video: e.target.value }))}
                  className="w-full bg-[#14101F] border border-white/[0.08] rounded-2xl px-3 py-2 text-sm outline-none focus:border-[#A03CF5]" />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-[#6E6683] font-semibold block mb-1">Voice · <CoinIcon size={10} className="align-[-1px]" /> per min</label>
                <input data-testid="rate-audio-input" type="number" min={1} value={cs.call_rate_audio}
                  onChange={(e) => setCs((c) => ({ ...c, call_rate_audio: e.target.value }))}
                  className="w-full bg-[#14101F] border border-white/[0.08] rounded-2xl px-3 py-2 text-sm outline-none focus:border-[#A03CF5]" />
              </div>
              <button data-testid="call-settings-save" onClick={saveCallSettings}
                className="self-end btn-gradient text-xs px-4 py-2.5">Save</button>
            </div>
          </div>

          {history.length > 0 && (
            <>
              <h2 className="text-sm font-bold text-white mt-7 mb-3">Call History</h2>
              <div className="space-y-1.5" data-testid="call-history">
                {history.slice(0, 10).map((c) => (
                  <div key={c.id} className="flex items-center gap-2.5 bg-[#14101F] border border-white/[0.06] rounded-2xl px-3.5 py-2.5">
                    <Avatar src={c.other?.avatar} size={32} tier={c.other?.tier} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white font-semibold truncate">{c.other?.name}</p>
                      <p className="text-[9px] text-[#6E6683]">{c.direction === "outgoing" ? "↗ Outgoing" : "↙ Incoming"} {c.mode} · {c.status}{c.minutes_billed ? ` · ${c.minutes_billed} min` : ""}</p>
                    </div>
                    <span className={`text-[10px] font-bold flex items-center gap-0.5 ${c.direction === "outgoing" ? "text-[#FFC857]" : "text-[#00E0C7]"}`}>
                      {c.direction === "outgoing" ? <>-{c.coins_spent} <CoinIcon size={11} /></> : <>+{c.diamonds_earned} <RubyIcon size={11} /></>}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <TopFans targetId={targetId} />

      <PostsSection targetId={targetId} isMe={isMe} targetName={p.name} />

      <h2 className="text-sm font-bold text-white mt-7 mb-3">Gift Wall <span className="text-[#FFD86B]">✦</span></h2>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="gift-wall">
        {data.gift_wall?.length === 0 && (
          <div className="w-full flex flex-col items-center py-5 text-[#6E6683]">
            <GiftIcon size={22} className="opacity-40 mb-1.5" />
            <p className="text-xs">No gifts received yet</p>
          </div>
        )}
        {data.gift_wall?.map((g, i) => (
          <div key={`${g.gift}-${i}`} className="shrink-0 w-24 bg-[#1E1830] border border-white/[0.06] rounded-2xl p-3 text-center">
            <p className="text-white text-xs font-bold truncate">{g.gift}</p>
            <p className="text-[#FF4D8D] text-[10px] font-semibold flex items-center justify-center gap-1 mt-0.5"><RubyIcon size={10} /> {g.diamonds?.toLocaleString()}</p>
          </div>
        ))}
      </div>
      {isMe && <BottomNav />}
    </div>
  );
}
